import { useId, useState } from 'react';

import { useResponsiveScale } from '../../hooks/useResponsiveRem';
import { Text } from '../../primitives/Text';
import {
  TextInput,
  type TextInputChangeEvent,
  type TextInputFocusEvent,
} from '../../primitives/TextInput';
import { View } from '../../primitives/View';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import { isTabInitiatedFocus } from '../../utils/keyboardFocus';
import { subTestID } from '../../utils/testId';
// Reuse Input's placeholder-color resolver and the keyboard-only focus-ring
// overlay so the two components stay pixel-identical.
import { resolveInputPlaceholderColor } from '../Input/Input.icons';
import { inputFocusRingVariants } from '../Input/Input.variants';
import type { TextareaProps } from './Textarea.types';
import {
  textareaFieldVariants,
  textareaHeightBounds,
  textareaRingVariants,
  textareaRootVariants,
  textareaTextInset,
  textareaTextVariants,
} from './Textarea.variants';

/**
 * Cross-platform multiline text field — a `<textarea>` on web, RN
 * `<TextInput multiline>` on native. Shares the shared Input's design tokens
 * (border / translucent surface / status / focus ring / label + helper) but
 * grows with content from `minRows` up to `maxRows`, then scrolls.
 *
 * Interaction states are automatic and match Input: the keyboard-only focus
 * ring (pointer/touch focus never rings), the darker "active typing" border,
 * and the filled value color all derive from focus + value.
 *
 * @example
 * ```tsx
 * import { Textarea } from '@flashka/shared/ui';
 *
 * <Textarea label="Notes" placeholder="Add a note…" minRows={3} maxRows={8} />
 * // Chat composer: single-line rest, grows to ~5 rows, send button inside.
 * <Textarea minRows={1} maxRows={5} placeholder="Message…" rightSlot={<SendButton />} />
 * ```
 */
export function Textarea({
  size = 'm',
  label,
  helperText,
  status,
  disabled = false,
  rightSlot,
  footerSlot,
  minRows = 3,
  maxRows = 8,
  fill = false,
  value,
  defaultValue,
  placeholder,
  ref,
  onChangeText,
  onFocus,
  onBlur,
  className,
  accessibilityLabel,
  testID,
  webProps,
  nativeProps,
}: TextareaProps) {
  const { isDark } = useTheme();
  const inputId = useId();
  const helperId = useId();
  const [focused, setFocused] = useState(false);
  // Keyboard-only focus ring — identical logic to Input: only a Tab keydown
  // rings it, so click/tap and programmatic `.focus()` stay ringless; native
  // never rings (touch is the only focus path). See `isTabInitiatedFocus`.
  const [keyboardFocus, setKeyboardFocus] = useState(false);
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue ?? '');
  const hasValue = (value ?? uncontrolledValue).length > 0;

  const state = focused ? (hasValue ? 'typing' : 'focused') : 'rest';
  const showFocusRing = keyboardFocus && !hasValue;

  const scale = useResponsiveScale();
  // The text box carries the field's vertical padding itself (see
  // `textareaTextInset`): a negative margin cancels the field's padding and an
  // equal padding puts the text back, so the scroll clip reaches the border.
  // Not at the bottom above a footer — there the gap belongs to the footer.
  const inset = textareaTextInset(size, scale);
  const insetBottom = footerSlot ? 0 : inset;
  const insetStyle = {
    marginTop: -inset,
    paddingTop: inset,
    marginBottom: -insetBottom,
    paddingBottom: insetBottom,
  };
  const { minHeight, maxHeight } = textareaHeightBounds(
    size,
    minRows,
    maxRows,
    scale,
    inset + insetBottom,
  );

  const handleChangeText = (text: string, event: TextInputChangeEvent) => {
    if (value === undefined) setUncontrolledValue(text);
    onChangeText?.(text, event);
  };
  const handleFocus = (event: TextInputFocusEvent) => {
    setFocused(true);
    setKeyboardFocus(isTabInitiatedFocus());
    onFocus?.(event);
  };
  const handleBlur = (event: TextInputFocusEvent) => {
    setFocused(false);
    setKeyboardFocus(false);
    onBlur?.(event);
  };

  // The text + trailing control. Reused as-is with no footer (direct children
  // of the flex-row field) or wrapped in a row above the footer when one is set.
  const fieldRow = (
    <>
      <TextInput
        ref={ref}
        testID={subTestID(testID, 'input')}
        multiline
        className={cn(
          textareaTextVariants({ size, focused }),
          // fill: the base `flex-1` grows to the field's height in the fill
          // column (flex-basis 0 wins over `field-sizing:content`); `min-h-0`
          // lets it shrink so `overflow-y:auto` scrolls instead of pushing out.
          fill && 'min-h-0',
        )}
        value={value}
        defaultValue={defaultValue}
        placeholder={placeholder}
        placeholderTextColor={resolveInputPlaceholderColor(focused, isDark)}
        onChangeText={handleChangeText}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
        accessibilityLabel={accessibilityLabel ?? label}
        webProps={{
          id: inputId,
          'aria-invalid': status === 'error' || undefined,
          'aria-describedby': helperText ? helperId : undefined,
          ...webProps,
          // fill: no height bounds — the flex column sizes it, `overflow-y:auto`
          // scrolls. Otherwise the min/max grow bounds (px): `field-sizing:content`
          // (variants) grows the textarea between them and scrolls past max.
          // Spread after `webProps` so the height rules always win.
          style: fill
            ? { overflowY: 'auto', ...insetStyle, ...webProps?.style }
            : {
                minHeight,
                maxHeight,
                overflowY: 'auto',
                ...insetStyle,
                ...webProps?.style,
              },
        }}
        nativeProps={{
          ...nativeProps,
          // fill: flex to the parent's height. Otherwise RN multiline auto-grows;
          // clamp it and let it scroll past max. The min floor applies only from
          // 2 rows up: a 1-row field must HUG the natural font line so the
          // row's items-center can vertically center it (iOS top-aligns text
          // inside a taller multiline box); the field's native min-height ramp
          // (textareaFieldVariants) keeps the resting height on spec instead.
          style: fill
            ? [
                { flex: 1 },
                insetStyle,
                (nativeProps as { style?: unknown } | undefined)?.style,
              ]
            : [
                minRows > 1 ? { minHeight, maxHeight } : { maxHeight },
                insetStyle,
                (nativeProps as { style?: unknown } | undefined)?.style,
              ],
        }}
      />
      {rightSlot ? (
        // self-end pins the trailing control to the bottom so it stays with
        // the last line as the field grows; the field itself centers a
        // single line (items-center).
        <View className="ml-1 shrink-0 self-end">{rightSlot}</View>
      ) : null}
    </>
  );

  return (
    <View
      className={cn(
        textareaRootVariants({ disabled }),
        fill && 'min-h-0 flex-1',
        className,
      )}
      testID={testID}>
      {label ? (
        <View className="flex h-6 w-full flex-row items-center">
          <Text
            as="label"
            className="font-medium text-b3 text-secondary-600 native:font-mona-medium"
            webProps={{ htmlFor: inputId }}>
            {label}
          </Text>
        </View>
      ) : null}

      {/* Field host anchors the absolute focus-ring overlay; both grow with the
          field so focusing never reflows content. */}
      <View className={cn(textareaRingVariants(), fill && 'min-h-0 flex-1')}>
        <View
          className={cn(
            textareaFieldVariants({
              size,
              state,
              status: status ?? 'none',
              rightInset: rightSlot ? 'slot' : 'text',
            }),
            // A footer OR fill stacks the field into a column: text on top,
            // footer pinned below. Without either, the field keeps its single
            // flex-row (unchanged from before these props existed).
            (footerSlot || fill) && 'flex-col items-stretch',
            // fill: the field grows to the parent's height so the text area can
            // stretch into it (see the `flex-1` textarea in fill mode).
            // `native:min-h-0` drops the size variant's native min-height ramp
            // (same-modifier conflict, so twMerge keeps this later class).
            fill && 'min-h-0 native:min-h-0 flex-1',
          )}>
          {footerSlot && !fill ? (
            // Footer without fill: the text row hugs its content above the footer.
            <View className="flex w-full flex-1 flex-row items-start">{fieldRow}</View>
          ) : (
            // fill (± footer) renders the textarea directly in the column so its
            // `flex-1` claims the height; default (no footer, no fill) keeps the
            // original flex-row.
            fieldRow
          )}
          {footerSlot ? <View className="mt-2 w-full shrink-0">{footerSlot}</View> : null}
        </View>
        {showFocusRing ? (
          <View
            className={inputFocusRingVariants({ size })}
            nativeProps={{ pointerEvents: 'none' }}
          />
        ) : null}
      </View>

      {helperText ? (
        <Text
          className={cn(
            'w-full font-medium text-b3 native:font-mona-medium',
            status === 'error'
              ? 'text-red-600'
              : status === 'success'
                ? 'text-green-600'
                : 'text-secondary-400',
          )}
          testID={subTestID(testID, 'helper')}
          webProps={{ id: helperId }}>
          {helperText}
        </Text>
      ) : null}
    </View>
  );
}
(Textarea as { displayName?: string }).displayName = 'Textarea';
